/**
 * useLocalProfileData Hook (REFACTORED)
 *
 * Now uses server API for certificates and social media links
 * instead of localStorage.
 *
 * Provides auto-save functionality with debouncing for social media.
 */

'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  useMyCertificates,
  useUploadCertificate,
  useDeleteCertificate,
  useMySocialMediaLinks,
  useUpdateSocialMediaLinks,
} from '../../../model/hooks';
import type { SocialMediaLink as ApiSocialMediaLink, SocialMediaLinkInput } from '../../../api';
import type { SocialMediaLink } from '../SocialMediaLinksSection';
import type { Certificate } from '../CertificatesSection';

interface UseLocalProfileDataOptions {
  userId?: string;
  enabled?: boolean;
  debounceMs?: number;
}

interface UseLocalProfileDataReturn {
  socialMediaLinks: SocialMediaLink[];
  setSocialMediaLinks: (links: SocialMediaLink[]) => void;
  certificates: Certificate[];
  setCertificates: (certs: Certificate[]) => void;
  addCertificates: (files: FileList) => Promise<void>;
  deleteCertificate: (id: string) => void;
  addSocialMediaLink: () => void;
  isLoading: boolean;
  isSaving: boolean;
}

/**
 * Convert API social media links to UI format
 */
function apiToUiSocialMediaLinks(apiLinks: ApiSocialMediaLink[]): SocialMediaLink[] {
  if (!apiLinks || apiLinks.length === 0) {
    // Return default empty link
    return [{ id: '1', platform: 'instagram', url: '' }];
  }

  return apiLinks.map((link) => ({
    id: link.id,
    platform: link.platform as SocialMediaLink['platform'],
    url: link.url,
  }));
}

/**
 * Convert UI social media links to API format (for batch update)
 */
function uiToApiSocialMediaLinks(uiLinks: SocialMediaLink[]): SocialMediaLinkInput[] {
  return uiLinks
    .filter((link) => link.url && link.url.trim() !== '')
    .map((link) => ({
      platform: link.platform,
      url: link.url,
    }));
}

export function useLocalProfileData({
  enabled = true,
  debounceMs = 500,
}: UseLocalProfileDataOptions = {}): UseLocalProfileDataReturn {
  // Certificates from server
  const {
    data: serverCertificates,
    isLoading: certificatesLoading,
  } = useMyCertificates(enabled);

  const uploadCertificateMutation = useUploadCertificate();
  const deleteCertificateMutation = useDeleteCertificate();

  // Social media from server
  const {
    data: serverSocialMedia,
    isLoading: socialMediaLoading,
  } = useMySocialMediaLinks(enabled);

  const updateSocialMediaMutation = useUpdateSocialMediaLinks();

  // Local state for social media (for optimistic updates and debouncing)
  const [localSocialMediaLinks, setLocalSocialMediaLinksState] = useState<SocialMediaLink[]>([
    { id: '1', platform: 'instagram', url: '' },
  ]);

  // Sync server social media to local state
  useEffect(() => {
    if (serverSocialMedia) {
      setLocalSocialMediaLinksState(apiToUiSocialMediaLinks(serverSocialMedia));
    }
  }, [serverSocialMedia]);

  // Debounce timer for social media auto-save
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSavingSocialMedia, setIsSavingSocialMedia] = useState(false);

  // Convert server certificates to UI format
  const certificates: Certificate[] = (serverCertificates || []).map((cert) => ({
    id: cert.id,
    imageUrl: cert.imageUrl,
    title: cert.title || undefined,
  }));

  // Update social media links with debounced save to server
  const setSocialMediaLinks = useCallback(
    (links: SocialMediaLink[]) => {
      setLocalSocialMediaLinksState(links);

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce the save to server
      setIsSavingSocialMedia(true);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const apiLinks = uiToApiSocialMediaLinks(links);
          if (apiLinks.length > 0) {
            await updateSocialMediaMutation.mutateAsync(apiLinks);
          }
        } catch (err) {
          console.error('[useLocalProfileData] Failed to save social media:', err);
        } finally {
          setIsSavingSocialMedia(false);
        }
      }, debounceMs);
    },
    [debounceMs, updateSocialMediaMutation]
  );

  // Set certificates (for compatibility - actually triggers server operations)
  const setCertificates = useCallback(
    (_certs: Certificate[]) => {
      // This is a no-op now - certificates are managed via addCertificates/deleteCertificate
      console.warn(
        '[useLocalProfileData] setCertificates is deprecated. Use addCertificates/deleteCertificate instead.'
      );
    },
    []
  );

  // Add new certificates from files (uploads to server)
  const addCertificates = useCallback(
    async (files: FileList) => {
      for (const file of Array.from(files)) {
        try {
          const title = file.name.replace(/\.[^/.]+$/, ''); // Remove file extension
          await uploadCertificateMutation.mutateAsync({ file, title });
        } catch (err) {
          console.error('[useLocalProfileData] Failed to upload certificate:', err);
        }
      }
    },
    [uploadCertificateMutation]
  );

  // Delete a certificate from server
  const deleteCertificate = useCallback(
    (id: string) => {
      deleteCertificateMutation.mutate(id);
    },
    [deleteCertificateMutation]
  );

  // Add a new social media link (local only, saved on blur/debounce)
  const addSocialMediaLink = useCallback(() => {
    const newLink: SocialMediaLink = {
      id: Date.now().toString(),
      platform: 'instagram',
      url: '',
    };
    setLocalSocialMediaLinksState((prev) => [...prev, newLink]);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const isLoading = certificatesLoading || socialMediaLoading;
  const isSaving =
    isSavingSocialMedia ||
    uploadCertificateMutation.isPending ||
    deleteCertificateMutation.isPending ||
    updateSocialMediaMutation.isPending;

  return {
    socialMediaLinks: localSocialMediaLinks,
    setSocialMediaLinks,
    certificates,
    setCertificates,
    addCertificates,
    deleteCertificate,
    addSocialMediaLink,
    isLoading,
    isSaving,
  };
}
